import {
  AppShell,
  ButtonStylesParams,
  ColorScheme,
  ColorSchemeProvider,
  MantineProvider,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { useState } from "react";
import { Provider } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import SidebarAdmin from "./components/SideBarAdmin";
import TopNavbarAdmin from "./components/TopNavbarAdmin";
import { UserRole } from "./enums/User";
import { AuthProvider } from "./hooks/useAuth";
import { NotificationsProvider } from "./hooks/useNotifications";
import AdminRoutes, { adminRoutes } from "./navigators/routes/AdminRoutes"; // Make sure this path is correct
import { authRoutes } from "./navigators/routes/auth";
import { LogoutListener } from "./navigators/wrapper/LogoutListener"; // Adjust the import path as needed
import ProtectedRoute from "./navigators/wrapper/ProtectedRoute";
import { persistor, store } from "./redux/redux-store/configureStore";
import Login from "./screens/auth/Signin";
import VerifyEmail from "./screens/auth/VerifyEmail";

function App() {
  const theme = useMantineTheme();
  const [colorScheme, setColorScheme] = useState<ColorScheme>("light");
  const toggleColorScheme = (value?: ColorScheme) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));
  const matches = useMediaQuery("(max-width: 992px)");
  const [opened, setOpened] = useState(false);

  const handleOpen = () => {
    setOpened((prev) => !prev);
  };

  function useShowHeader() {
    const location = useLocation(); // from react-router-dom
    return !location.pathname.startsWith("/auth");
  }

  // Inside your AppShell component
  const showHeader = useShowHeader();
  const user = location.pathname.startsWith("/admin") ? "admin" : "";
  // Your existing setup
  return (
    // Existing Provider and MantineProvider setup
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AuthProvider>
          <ColorSchemeProvider
            colorScheme={colorScheme}
            toggleColorScheme={toggleColorScheme}
          >
            <MantineProvider
              withGlobalStyles
              withNormalizeCSS
              theme={{
                colorScheme: colorScheme,
                fontFamily: "Nunito Sans, sans-serif",
                fontFamilyMonospace: "Liberation Mono, Courier, monospace",
                headings: { fontFamily: "Nunito Sans, sans-serif" },

                components: {
                  Text: {
                    styles: (
                      theme,
                      params: ButtonStylesParams,
                      { variant }
                    ) => ({
                      root: {
                        color:
                          theme.colorScheme == "dark"
                            ? theme.white
                            : theme.black,
                        fontWeight: 400,
                      },
                    }),
                  },
                  TextInput: {
                    styles: (
                      theme,
                      params: ButtonStylesParams,
                      { variant }
                    ) => ({
                      root: {
                        backgroundColor:
                          theme.colorScheme == "dark"
                            ? theme.black
                            : theme.white,
                      },
                    }),
                  },
                },
              }}
            >
              <ModalsProvider>
                <Notifications position="top-center" zIndex={2077} />
                <NotificationsProvider url="">
                  <AppShell
                    padding={0}
                    navbarOffsetBreakpoint={992}
                    layout={matches ? "default" : "alt"}
                    navbar={
                      showHeader ? (
                        user == "admin" ? (
                          <SidebarAdmin
                            closeNavBar={() => setOpened(false)}
                            opened={opened}
                          />
                        ) : (
                          <></>
                        )
                      ) : (
                        <></>
                      )
                    }
                    header={
                      showHeader ? (
                        user == "admin" ? (
                          <TopNavbarAdmin
                            opened={opened}
                            handleOpen={handleOpen}
                          />
                        ) : (
                          <></>
                        )
                      ) : (
                        <></>
                      )
                    }
                  >
                    <LogoutListener />
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <Navigate
                            replace
                            to={"/admin/" + adminRoutes.COMPLAINT_CENTER}
                          />
                        }
                      />
                      <Route path={authRoutes.LOGIN} element={<Login />} />
                      <Route
                        path={authRoutes.VERIFY_ACCOUNT}
                        element={<VerifyEmail />}
                      />
                      {/* <Route path={authRoutes.FORGOT_PASSWORD}/> */}
                      {/* <Route path={authRoutes.RESET_PASSWORD} element={<VerifyEmail />} /> */}
                      {/* <Route
                    path={userRoutes.DASHBOARD}
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  /> */}
                      {/* <Route
                    path={userRoutes.PAYMENT_FEEDBACK}
                    element={
                      <ProtectedRoute>            
                        <PaymentFeedback />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={userRoutes.PAYMENT}
                    element={
                      <ProtectedRoute>            
                        <PricingPage />
                      </ProtectedRoute>
                    }
                  /> */}
                      {/* <Route 
                    path="/parent/*"
                    element={
                        <ProtectedRoute>
                          <ParentRoutes />
                        </ProtectedRoute>
                      }
                  /> */}
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute allowedRoles={[UserRole.admin]}>
                            <AdminRoutes />
                          </ProtectedRoute>
                        }
                      />
                      {/* Add other routes as needed */}
                    </Routes>
                  </AppShell>
                </NotificationsProvider>
              </ModalsProvider>
            </MantineProvider>
          </ColorSchemeProvider>
        </AuthProvider>
      </PersistGate>
    </Provider>
    // Closing tags for Provider and MantineProvider
  );
}

export default App;
